import { useEffect, useRef } from "react";
import { Rocket, Building2, Clock, Zap } from "lucide-react";
import { motion, useMotionValue } from "framer-motion";
import Image from "next/image";
import { PARTNERS } from "@/resources/constants";
import crossLinesPattern from "@/assets/svg/crosslines.svg";
import { useTranslation } from "next-i18next";

const metricIcons = [
  { icon: Rocket, color: "electric-blue" },      // Live Deployments
  { icon: Building2, color: "transport-mint" },  // Industries Served
  { icon: Clock, color: "retail-coral" },        // 24/7 AI Monitoring
  { icon: Zap, color: "industry-lilac" },        // Days To Deploy (fast)
];

const TransformationProof = () => {
  const { t } = useTranslation('home');

  // Partner carousel setup
  const itemWidth = 140;
  const allPartners = [...PARTNERS, ...PARTNERS]; // Duplicate for seamless loop
  const totalItemsCount = allPartners.length;
  const speed = 1;
  const carouselRef = useRef(null);
  const x = useMotionValue(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      let newX = x.get() - speed;
      if (newX < -totalItemsCount * itemWidth / 2) {
        newX = 0;
      }
      x.set(newX);
    }, 20);
    return () => clearInterval(intervalId);
  }, [x, totalItemsCount]);

  return (
    <motion.section
      className="pt-20 md:pt-24"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold">
            {t('transformationProof.title')}
          </h2>
        </motion.div>

        {/* Metrics Dashboard */}
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 md:mb-20 max-w-6xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {metricIcons.map((metricIcon, index) => (
            <motion.div
              key={index}
              className="bg-container-background border border-border rounded-xl p-6 hover:border-opacity-60 transition-all duration-300 light_effect_card relative overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Background pattern */}
              <div
                className="absolute inset-0 rounded-xl pointer-events-none"
                style={{
                  backgroundImage: `url(${crossLinesPattern.src})`,
                  backgroundPosition: "center",
                  backgroundRepeat: "repeat",
                  backgroundSize: "60px",
                  opacity: 0.15,
                  maskImage: "radial-gradient(ellipse 150% 150% at top right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 25%, rgba(0,0,0,0) 50%)",
                  WebkitMaskImage: "radial-gradient(ellipse 150% 150% at top right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 25%, rgba(0,0,0,0) 50%)"
                }}
              />

              {/* Gradient overlay */}
              {/* <div className="absolute inset-0 rounded-xl pointer-events-none card-gradient-overlay-primary opacity-50"></div> */}

              <div className="relative z-10">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 relative overflow-hidden"
                  style={{ backgroundColor: `hsl(var(--${metricIcon.color}) / 0.15)` }}
                >
                  <metricIcon.icon
                    className="w-6 h-6 relative z-10"
                    style={{ color: `hsl(var(--${metricIcon.color}))` }}
                  />
                </div>
                <div
                  className="text-2xl md:text-3xl font-bold mb-2"
                  style={{ color: `hsl(var(--${metricIcon.color}))` }}
                >
                  {t(`transformationProof.metrics.${index}.value`)}
                </div>
                <p className="text-primary-foreground text-sm font-semibold mb-2">{t(`transformationProof.metrics.${index}.label`)}</p>
                <p className="text-secondary-foreground text-xs leading-relaxed">{t(`transformationProof.metrics.${index}.description`)}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Partner Logos Carousel */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          viewport={{ once: true }}
        >
          <p className="text-sm text-secondary-foreground mb-8">{t('transformationProof.partners')}</p>

          {/* Carousel Container */}
          <div className="flex overflow-hidden mx-auto max-w-6xl">
            <motion.div
              className="flex gap-4"
              ref={carouselRef}
              style={{ x }}
            >
              {allPartners.map((partner, index) => {
                return (
                  <div
                    key={index}
                    className="rounded-lg bg-container-background border border-border px-4 py-2 flex items-center justify-center min-w-[140px] md:min-w-[160px] h-20 transition-opacity hover:opacity-80"
                  >
                    <Image
                      height={80}
                      width={160}
                      className="grayscale h-12 md:h-16 lg:h-20 w-auto object-contain opacity-60"
                      src={partner}
                      alt="Partner logo"
                    />
                  </div>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default TransformationProof;
